import {
  CTBProgram,
  ProgramAPIResponse,
  ProgramTarget,
  ProgramType,
  parseProgramDetails
} from "./programs/parsePrograms";
import { ProgramFilters } from "../../hooks/programs/usePrograms";
import { api, createURLSearchParams } from "../api";

type PaginatedProgramData = {
  programs: CTBProgram[];
  results: number;
};

export type ProgramQueryFilters = {
  activated?: string;
  deleted?: string;
  private?: string;
  type?: string;
  limit?: string;
  page?: string;
  search?: string;
};

export type ProgramUpdateFields = {
  title?: string;
  description?: string;
  scope?: string;
  outOfScope?: string;
  targets?: ProgramTarget[];
  type?: ProgramType;
  triaged?: boolean;
  payoutRange?: number[];
  knownVulnerabilities?: string;
  other?: string;
  vpn?: string;
  termsOfService?: string;
  credentials?: string;
  rewardPolicy?: string;
  isDelete?: boolean;
  private?: boolean;
  jira_url?: string;
  jira_email?: string;
  jira_api_token?: string;
  jira_project_key?: string;
};

const programsUrl = "/v2/programs";
const legacyProgramsUrl = "/programs"; // For v1 endpoints still in use
const publicProgramUrl = "/public-program";

/**
 * The Programs API slice definition.
 *
 * Each endpoint is defined here, and is then available via the
 * generated hooks. Data fetched using these endpoints is cached
 * for any subsequent queries
 */
export const programsApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Per-program severity stats (supports validOnly)
     */
    getProgramStats: builder.query<
      { critical: number; high: number; medium: number; low: number; info: number },
      { programId: number; validOnly?: boolean }
    >({
      query: ({ programId, validOnly = true }) => ({
        url: `${programsUrl}/get-program-stats`,
        method: "get",
        params: { id: programId, validOnly }
      }),
      transformResponse: (data: any) => {
        const s = (data?.severities || {}) as Record<string, number>;
        return {
          critical: Number(s.Critical || s.critical || 0),
          high: Number(s.High || s.high || 0),
          medium: Number(s.Medium || s.medium || 0),
          low: Number(s.Low || s.low || 0),
          info: Number(s.Info || s.info || 0)
        };
      },
      providesTags: (_, __, { programId }) => [
        { type: "Program", id: programId }
      ]
    }),
    /**
     * Saves program details like test lead, prepared by, etc.
     */
    saveProgramDetails: builder.mutation<
      any, // Replace with proper response type if known
      {
        programId: number;
        details: {
          test_lead: string;
          prepared_by: string;
          reviewed_by: string;
          approved_by: string;
        };
      }
    >({
      query: ({ programId, details }) => ({
        url: `${programsUrl}/${programId}/details`,
        method: "post",
        data: details
      }),
      invalidatesTags: (_, __, { programId }) => [
        {
          type: "Program",
          id: programId
        }
      ]
    }),
    /**
     * Retrieve the list of paginated programs available to
     * the current user from the backend
     */
    getPrograms: builder.query<PaginatedProgramData, ProgramFilters>({
      query: filters => {
        const params = createURLSearchParams<ProgramQueryFilters>({
          activated: filters.activated?.map(a => a === "Active").join(","),
          deleted: filters.deleted?.join(","),
          private: filters.private?.map(p => p === "Private").join(","),
          type: filters.type,
          limit: filters.limit?.toString(),
          page: filters.page?.toString(),
          search: filters.search
        });

        return {
          url: programsUrl + "?" + params.toString(),
          method: "get"
        };
      },
      transformResponse: (response: {
        programs: ProgramAPIResponse[];
        count: number;
      }) => ({
        programs: (response?.programs || [])
          .filter(program => program !== undefined && program !== null)
          .map(program => parseProgramDetails(program))
          .filter(Boolean),
        results: response?.count || 0
      }),
      providesTags: ["Programs"]
    }),
    /**
     * Retreive a single program from the backend
     */
    getProgram: builder.query<CTBProgram, number>({
      query: id => ({ url: programsUrl + "?id=" + id, method: "get" }),
      transformResponse: (response: {
        programs: ProgramAPIResponse[];
        count: number;
      }) => {
        if (
          !response ||
          !Array.isArray(response.programs) ||
          response.programs.length === 0 ||
          !response.programs[0]
        ) {
          return null as any;
        }
        return parseProgramDetails(response.programs[0]);
      },
      providesTags: (_, __, id) => [
        {
          type: "Program",
          id
        }
      ]
    }),
    /**
     * Retreive a single program from the backend for public page
     */
    getPublicProgram: builder.query<CTBProgram, number>({
      query: id => ({ url: publicProgramUrl + "?id=" + id, method: "get" }),
      transformResponse: (response: ProgramAPIResponse) =>
        response ? parseProgramDetails(response) : (null as any),
      providesTags: (_, __, id) => [
        {
          type: "Program",
          id
        }
      ]
    }),
    /**

     * Updates the list of private access users for the given program
     */
    setPrivateAccessUsers: builder.mutation<
      void,
      {
        id: number;
        users: number[];
      }
    >({
      query: ({ id, users }) => ({
        url: programsUrl + "/" + id,
        method: "post",
        data: {
          private_users: users
        }
      }),
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),
    /**
     * Activates/deactivates the given program
     */
    postProgramActivation: builder.mutation<
      void,
      {
        id: number;
        isActivated: boolean;
      }
    >({
      query: ({ id, isActivated }) => ({
        url: programsUrl + "/" + id,
        method: "post",
        data: {
          activated: isActivated
        }
      }),
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),
    /**
     * Deletes the given program
     */
    deleteProgram: builder.mutation<void, number>({
      query: id => ({ url: programsUrl + "?id=" + id, method: "delete" }),
      invalidatesTags: (_, __, id) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),
    /**
     * Updates the given program, creating a new one if necessary
     */
    updateProgram: builder.mutation<number, { id?: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: programsUrl + (id === undefined ? "" : "/" + id),
        method: "post",
        data
      }),
      transformResponse: (response: { id: number }) => response.id,
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),
    /**
     * Updates the given program dates (Admin/Sub-Admin only)
     */
    updateProgramDates: builder.mutation<
      { message: string; id: number },
      { id: number; expected_start_date: string; expected_end_date: string }
    >({
      query: ({ id, ...dates }) => ({
        url: `${programsUrl}/${id}/dates`,
        method: "patch",
        data: dates
      }),
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),
    /**
     * Updates the given program using V2 endpoint
     */
    updateProgramV2: builder.mutation<number, { id?: number; data: FormData }>({
      query: ({ id, data }) => {
        if (id !== undefined) {
          data.append("program_id", id.toString());
        }
        return {
          url: `${programsUrl}/v2-save${id === undefined ? "" : "/" + id}`,
          method: "post",
          data
        };
      },
      transformResponse: (response: { id: number }) => response.id,
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),


    // Add attachments handling in the mutation
    updateProgramWithAttachments: builder.mutation<
      number,
      { id?: number; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: programsUrl + (id === undefined ? "" : "/" + id),
        method: "post",
        data
      }),
      transformResponse: (response: { id: number }) => response.id,
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Program",
          id
        },
        "Programs"
      ]
    }),
    /**
     * Update a program phase (pretesting -> active_reporting)
     * Uses legacy v1 endpoint under /api/programs/:id/mark-ready
     */
    updateProgramPhase: builder.mutation<
      { message: string; phase_status: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `${legacyProgramsUrl}/${id}/mark-ready`,
        method: "post"
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "Program", id },
        "Programs"
      ]
    }),


    // Delegation configuration endpoints
    getProgramDelegationConfig: builder.query<
      {
        auto_delegation_enabled: boolean;
        auto_delegation_mode: "round_robin" | "random" | "manual_only";
        auto_delegation_threshold_hours?: number;
        auto_delegation_reassign_window_hours?: number;
        auto_delegation_max_attempts?: number;
        auto_delegation_paused?: boolean;
      },
      { programId: number }
    >({
      query: ({ programId }) => ({ url: `${programsUrl}/${programId}/delegation-config`, method: "get" }),
      transformResponse: (resp: any) => resp?.data ?? resp,
      providesTags: (_, __, { programId }) => [{ type: "Program", id: programId }]
    }),

    updateProgramDelegationConfig: builder.mutation<
      { message: string; data: any },
      { programId: number; data: Partial<{ auto_delegation_enabled: boolean; auto_delegation_mode: "round_robin" | "random" | "manual_only"; auto_delegation_threshold_hours?: number; auto_delegation_reassign_window_hours?: number; auto_delegation_max_attempts?: number; auto_delegation_paused?: boolean; }> }
    >({
      query: ({ programId, data }) => ({ url: `${programsUrl}/${programId}/delegation-config`, method: "patch", data }),
      invalidatesTags: (_, __, { programId }) => [{ type: "Program", id: programId }]
    }),

    pauseProgramDelegation: builder.mutation<{ message: string }, { programId: number }>({
      query: ({ programId }) => ({ url: `${programsUrl}/${programId}/delegation/pause`, method: "post" }),
      invalidatesTags: (_, __, { programId }) => [{ type: "Program", id: programId }]
    }),

    resumeProgramDelegation: builder.mutation<{ message: string }, { programId: number }>({
      query: ({ programId }) => ({ url: `${programsUrl}/${programId}/delegation/resume`, method: "post" }),
      invalidatesTags: (_, __, { programId }) => [{ type: "Program", id: programId }]
    }),

    /**
     * Get all targets for an asset
     */
    getAssetTargets: builder.query<any[], string>({
      query: assetId => ({ url: `/assets/${assetId}/targets`, method: "get" })
    }),

    /**
     * Get all resources for a target
     */
    getTargetResources: builder.query<any[], { assetId: string; targetId: string; includeDiscovered?: boolean }>({
      query: ({ assetId, targetId, includeDiscovered }) => ({
        url: `/assets/${assetId}/targets/${targetId}/resources`,
        method: "get",
        params: includeDiscovered ? { include_discovered: "true" } : undefined
      }),
      providesTags: (result, error, { targetId }) => [{ type: "ChecklistItems", id: `target-${targetId}` }]
    }),

    /**
     * Get run scope snapshot
     */
    getRunScopeSnapshot: builder.query<{ snapshot: any; is_scope_verified: boolean; is_scope_submitted: boolean; status: string }, number>({
      query: runId => ({ url: `/v2/programs/runs/${runId}/scope`, method: "get" }),
      transformResponse: (resp: any) => resp.data,
      providesTags: (result, error, runId) => [{ type: "ProgramRuns", id: runId }]
    }),

    /**
     * Get scope options (asset, targets, endpoints) from a run's snapshot
     * Used for report creation to populate options from snapshot
     */
    getRunScopeOptions: builder.query<{
      asset: any;
      targets: any[];
      endpointsByTarget: Record<string, any[]>;
    }, number>({
      query: runId => ({ url: `/v2/programs/runs/${runId}/scope-options`, method: "get" }),
      transformResponse: (resp: any) => resp.data,
      providesTags: (result, error, runId) => [{ type: "ProgramRuns", id: runId }]
    })

  })
});

export const {
  useGetProgramsQuery,
  useGetProgramQuery,
  useGetPublicProgramQuery,
  useGetProgramStatsQuery,
  useSetPrivateAccessUsersMutation,
  usePostProgramActivationMutation,
  useDeleteProgramMutation,
  useUpdateProgramMutation,
  useUpdateProgramWithAttachmentsMutation,
  useSaveProgramDetailsMutation,
  useUpdateProgramPhaseMutation,
  useGetProgramDelegationConfigQuery,
  useUpdateProgramDelegationConfigMutation,
  usePauseProgramDelegationMutation,
  useResumeProgramDelegationMutation,
  useUpdateProgramV2Mutation,
  useUpdateProgramDatesMutation,
  useGetAssetTargetsQuery,
  useGetTargetResourcesQuery,
  useGetRunScopeSnapshotQuery,
  useGetRunScopeOptionsQuery
} = programsApi;
